import React, { FC, ChangeEvent, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import cn from 'classnames';

import { Checkbox } from '@azgaz/azgaz-ui';
import {
  actions as carActions,
  selectors as carSelectors,
} from '@/apps/appCreator/store/car';
import { config, format, WidgetMode } from '@/utils';
import { getCreditCalcSelectedProgramm } from '@/selectors';
import { TBrandCar } from '@/selectors/getBrandCar';
import { Title, Tooltip, Icon } from '@/apps/appCreator/components';
import CarDiscounts, { Item } from '@/apps/appCreator/components/CarDiscounts';
import { Disclaimers, TSortedDisclaimers } from '@/selectors/getCarDisclaimers';
import { TOOLTIP_TEXT } from '@/constants';

import style from './style.module.scss';

type TCarPriceDetails = {
  car: TBrandCar;
  disclaimers: TSortedDisclaimers;
};

const CarPriceDetails: FC<TCarPriceDetails> = ({ car, disclaimers }) => {
  const dispatch = useDispatch();
  const discounts = useSelector(carSelectors.selectDiscounts);
  const currentCreditProgramm = useSelector(getCreditCalcSelectedProgramm);
  const featureFlagTradeIn = config.get('featureFlagTradeIn');
  const isDealerMode = WidgetMode.isDealerMode();

  const clickDiscountHandler = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, checked } = e.target;
      const value = car?.extra[name];

      dispatch(
        carActions.updateDiscountSelected({
          keyName: name,
          value,
          selected: checked,
        })
      );
    },
    [car]
  );

  useEffect(() => {
    dispatch(carActions.clearDiscountsSelected());
  }, [car?.id]);

  /**
   * Выгода SuzukiFinance применяется/отключается автоматически,
   * в зависимости от выбранной кредитной программы
   */
  useEffect(() => {
    const value = car?.extra['resolvedSuzukiFinanceDiscount'] || 0;

    dispatch(
      carActions.updateDiscountSelected({
        keyName: 'resolvedSuzukiFinanceDiscount',
        value,
        selected: !currentCreditProgramm?.isDisabledSuzukiFinance,
      })
    );
  }, [currentCreditProgramm?.current]);

  return car && (car.extra.hasDiscounts || featureFlagTradeIn) ? (
    <CarDiscounts
      className={style.carDiscounts}
      total={`${format.price(isDealerMode ? car.extra.dealerCarPriceDiscounted : car.extra.carPriceDiscounted)} РУБ.`}
    >
      <Item
        className={style.discountItemTitle}
        name={
          <Title tag="h4" className={style.titleH4}>
            {isDealerMode ? 'Цена у дилера' : 'Рекомендованная розничная цена'}
          </Title>
        }
        disclaimerLink={disclaimers.get(Disclaimers.price)?.sortOrder}
        tooltip={
          <Tooltip title={isDealerMode ? TOOLTIP_TEXT.dealerPriceRetail : TOOLTIP_TEXT.priceRetail} placement="bottom">
            <Icon name="info" className="ml-2" />
          </Tooltip>
        }
      >
        <span className={style.carPrice}>
          {format.price(isDealerMode ? car.extra.dealerPrice : car.extra.price)} руб.
        </span>
      </Item>
      {featureFlagTradeIn && (
        <Item
          className={cn(style.discountItem, {
            [style.selected]: discounts.tradeInPrice.selected,
            [style.disabled]: car.extra.tradeInPrice === 0,
          })}
          name={
            <>
              <Checkbox
                value={discounts.tradeInPrice.selected}
                onChange={clickDiscountHandler}
                name="tradeInPrice"
              />
              Стоимость вашего автомобиля
            </>
          }
          tooltip={
            <Tooltip title={TOOLTIP_TEXT.tradeInPrice} placement="bottom">
              <Icon name="info" className="ml-2" />
            </Tooltip>
          }
        >
          {car.extra.tradeInPrice
            ? `-${format.price(car.extra.tradeInPrice)} руб.`
            : '0 руб.'}
        </Item>
      )}
      {Boolean(car.extra.tradeInDiscount) && (
        <Item
          className={cn(style.discountItem, {
            [style.selected]: discounts.resolvedTradeInDiscount.selected,
          })}
          name={
            <>
              <Checkbox
                value={discounts.resolvedTradeInDiscount.selected}
                onChange={clickDiscountHandler}
                name="resolvedTradeInDiscount"
              />
              Выгода по программе трейд-ин
            </>
          }
          disclaimerLink={disclaimers.get(Disclaimers.tradein)?.sortOrder}
          tooltip={
            <Tooltip title={TOOLTIP_TEXT.tradeIn} placement="bottom">
              <Icon name="info" className="ml-2" />
            </Tooltip>
          }
        >
          {discounts.resolvedTradeInDiscount.selected
            ? `-${format.price(car.extra.resolvedTradeInDiscount || 0)} руб.`
            : `до ${format.price(car.extra.tradeInDiscount)} руб.`}
        </Item>
      )}
      {car.extra.suzukiFinanceDiscount && (
        <Item
          className={cn(style.discountItem, style.disabled, {
            [style.selected]: discounts.resolvedSuzukiFinanceDiscount.selected,
          })}
          name={
            <>
              <Checkbox
                value={discounts.resolvedSuzukiFinanceDiscount.selected}
                onChange={clickDiscountHandler}
                name="resolvedSuzukiFinanceDiscount"
              />
              Выгода по программе Suzuki Finance
            </>
          }
          disclaimerLink={disclaimers.get(Disclaimers.credit)?.sortOrder}
          tooltip={
            <Tooltip title={TOOLTIP_TEXT.suzukiFinance} placement="bottom">
              <Icon name="info" className="ml-2" />
            </Tooltip>
          }
        >
          {discounts.resolvedSuzukiFinanceDiscount.selected
            ? `-${format.price(
                car.extra.resolvedSuzukiFinanceDiscount || 0
              )} руб.`
            : `до ${format.price(car.extra.suzukiFinanceDiscount)} руб.`}
        </Item>
      )}
      {car.extra.medicalDiscount && (
        <Item
          className={cn(style.discountItem, {
            [style.selected]: discounts.medicalDiscount.selected,
          })}
          name={
            <>
              <Checkbox
                value={discounts.medicalDiscount.selected}
                onChange={clickDiscountHandler}
                name="medicalDiscount"
              />
              Выгода для работников медицинских учреждений
            </>
          }
          disclaimerLink={disclaimers.get(Disclaimers.medical)?.sortOrder}
        >
          -{format.price(car.extra.medicalDiscount)} руб.
        </Item>
      )}
      {car.extra.dealerDiscount && isDealerMode && (
        <Item
          className={cn(style.discountItem, {
            [style.selected]: discounts.dealerDiscount.selected,
          })}
          name={
            <>
              <Checkbox
                value={discounts.dealerDiscount.selected}
                onChange={clickDiscountHandler}
                name="dealerDiscount"
              />
              Выгода от дилерского центра
            </>
          }
        >
          -{format.price(car.extra.dealerDiscount)} руб.
        </Item>
      )}
    </CarDiscounts>
  ) : null;
};

export default CarPriceDetails;
